import { createAsyncThunk } from '@reduxjs/toolkit';
import { logger } from '../../helpers/logger';

/**
 * Factory to create a keyed async thunk plus a helper to attach lifecycle reducers.
 * This reduces boilerplate for the common idle/pending/fulfilled/rejected pattern
 * while supporting request de-dupe via `condition` and caching results by a dynamic key.
 *
 * Options:
 * - typePrefix: string used by createAsyncThunk (e.g., 'dropdowns/loadTaskTags')
 * - getCacheKey: (arg) => string unique key for this request instance
 * - call: (arg, thunkAPI) => Promise<any> actual async call; should throw or return a value
 * - selectPayload: (result, arg, thunkAPI) => any transform of result for storage (default: passthrough)
 * - selectRequestsFromRoot: (rootState) => object map for request statuses to support condition/dedupe
 * - requestsKey: name of the map on the slice state for statuses (e.g., 'taskTagsRequests')
 * - dataKey: name of the map on the slice state for cached data (e.g., 'taskTagsData')
 */
export function createKeyedThunk({
    typePrefix,
    getCacheKey,
    call,
    selectPayload = (res) => res,
    wasSuccessful = (res) => !!res?.wasSuccessful,
    rejectPayload,
    selectRequestsFromRoot,
    requestsKey,
    dataKey,
}) {
    // Validate required parameters - use console.error + return invalid thunk instead of throwing
    const errors = [];
    if (!typePrefix) errors.push('typePrefix is required');
    if (!getCacheKey) errors.push('getCacheKey is required');
    if (!call) errors.push('call is required');
    if (!selectRequestsFromRoot) errors.push('selectRequestsFromRoot is required');
    if (!requestsKey) errors.push('requestsKey is required');
    if (!dataKey) errors.push('dataKey is required');

    if (errors.length > 0) {
        logger.error('createKeyedThunk validation errors:', errors);
        // Return a thunk that always rejects instead of throwing synchronously
        const invalidThunk = createAsyncThunk(
            typePrefix || 'invalid/thunk',
            async (arg, thunkAPI) => {
                return thunkAPI.rejectWithValue({
                    configurationError: `Thunk configuration invalid: ${errors.join(', ')}`,
                    wasSuccessful: false,
                });
            },
        );

        const addCases = (builder) => {
            builder.addCase(invalidThunk.rejected, (state, action) => {
                logger.error('Invalid thunk executed:', action.payload.configurationError);
            });
        };

        return { thunk: invalidThunk, addCases };
    }

    const thunk = createAsyncThunk(
        typePrefix,
        async (arg, thunkAPI) => {
            try {
                const result = await call(arg, thunkAPI);
                if (!wasSuccessful(result, arg, thunkAPI)) {
                    return thunkAPI.rejectWithValue(
                        typeof rejectPayload === 'function'
                            ? rejectPayload(result, arg, thunkAPI)
                            : result,
                    );
                }
                return selectPayload(result, arg, thunkAPI);
            } catch (error) {
                // If it's a ValidationError from handleApiCall, convert it to a rejection
                // This allows the component error system to work while still properly
                // tracking the thunk as rejected
                if (error?.name === 'ValidationError') {
                    return thunkAPI.rejectWithValue({
                        errorMessage: error.message,
                        validationErrors: error.validationErrors,
                        statusCode: error.statusCode,
                        wasSuccessful: false,
                        ...error.result,
                    });
                }

                // For any other errors, use a silent rejection to avoid uncaught promise errors
                return thunkAPI.rejectWithValue({
                    silent: true,
                    error: error.message,
                    wasSuccessful: false,
                });
            }
        },
        {
            condition: (arg, { getState }) => {
                try {
                    const cacheKey = getCacheKey(arg);
                    const requests = selectRequestsFromRoot(getState());
                    const status = requests?.[cacheKey];
                    // Don't fetch if already fulfilled or currently loading
                    if (status === 'fulfilled' || status === 'pending') return false;
                } catch (_) {
                    // If selection fails, allow fetch rather than blocking
                }
                return true;
            },
        },
    );

    const addCases = (builder) => {
        builder
            .addCase(thunk.pending, (state, action) => {
                const cacheKey = getCacheKey(action.meta.arg);
                state[requestsKey] = state[requestsKey] || {};
                state[dataKey] = state[dataKey] || {};
                state[requestsKey][cacheKey] = 'pending';
            })
            .addCase(thunk.fulfilled, (state, action) => {
                const cacheKey = getCacheKey(action.meta.arg);
                state[requestsKey][cacheKey] = 'fulfilled';
                state[dataKey][cacheKey] = action.payload;
            })
            .addCase(thunk.rejected, (state, action) => {
                const cacheKey = getCacheKey(action.meta.arg);
                state[requestsKey][cacheKey] = 'rejected';
            });
    };

    return { thunk, addCases };
}

/**
 * Aggregates multiple keyed thunks sharing the same slice locations for requests/data maps.
 *
 * Config:
 * - sliceSelector: (root) => sliceState
 * - requestsKey: string
 * - dataKey: string
 * - baseTypePrefix?: string (e.g., 'dropdowns')
 * - endpoints: {
 *     [name]: {
 *       typePrefix?: string,
 *       getCacheKey: (arg) => string,
 *       call: (arg, thunkAPI) => Promise<any>,
 *       isSuccessful?: (result, arg, thunkAPI) => boolean,
 *       rejectPayload?: (result, arg, thunkAPI) => any,
 *       selectPayload?: (result, arg, thunkAPI) => any,
 *     }
 *   }
 */
export function createKeyedThunks({
    sliceName,
    sliceSelector,
    requestsKey,
    dataKey,
    baseTypePrefix,
    endpoints,
}) {
    // Validate required parameters - log errors instead of throwing
    const errors = [];
    if (!sliceSelector && !sliceName) errors.push('Provide either sliceSelector or sliceName');
    if (!requestsKey) errors.push('requestsKey is required');
    if (!dataKey) errors.push('dataKey is required');
    if (!endpoints || typeof endpoints !== 'object') errors.push('endpoints map is required');

    if (errors.length > 0) {
        logger.error('createKeyedThunks validation errors:', errors);
        // Return empty collections instead of throwing
        return {
            thunks: {},
            extraReducers: (builder) => {
                logger.error('Invalid thunks configuration - no reducers added');
            },
        };
    }

    const factories = {};
    const thunks = {};

    const selectRequestsFromRoot = (root) => {
        const slice = typeof sliceSelector === 'function' ? sliceSelector(root) : root?.[sliceName];
        return slice?.[requestsKey];
    };

    const effectiveBase = baseTypePrefix || sliceName;

    Object.entries(endpoints).forEach(([name, cfg]) => {
        const typePrefix = cfg.typePrefix || (effectiveBase ? `${effectiveBase}/${name}` : null);
        if (!typePrefix) {
            logger.error(`typePrefix missing for endpoint '${name}' - skipping this endpoint`);
            return; // Skip this endpoint instead of throwing
        }

        const factory = createKeyedThunk({
            typePrefix,
            getCacheKey: cfg.getCacheKey,
            call: cfg.call,
            wasSuccessful: cfg.wasSuccessful,
            rejectPayload: cfg.rejectPayload,
            selectPayload: cfg.selectPayload,
            selectRequestsFromRoot,
            requestsKey,
            dataKey,
        });
        factories[name] = factory;
        thunks[name] = factory.thunk;
    });

    const extraReducers = (builder) => {
        Object.values(factories).forEach((f) => f.addCases(builder));
    };

    return { thunks, extraReducers };
}
